import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Dropdown, Tag, message, Input, InputNumber } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, PoweroffOutlined, MoreOutlined } from "@ant-design/icons";

import {
  getSalesPersonsApi,
  createSalesPersonApi,
  updateSalesPersonApi,
  deleteSalesPersonApi,
} from "../../services/api";
import AppTable from "../../common/AppTable";
import AppInput from "../../common/FormInputs/AppInput";

const ACCENT = "#534ab7";

export default function SalesPersons() {
  const [people,  setPeople]  = useState([]);
  const [open,    setOpen]    = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchPeople = async () => {
    try {
      const res = await getSalesPersonsApi();
      setPeople((res.data.data || []).slice().sort((a, b) => b.id - a.id));
    } catch {
      message.error("Failed to load sales team");
    }
  };

  useEffect(() => { fetchPeople(); }, []);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ monthlyTarget: 10, commissionRatePercent: 10 });
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      monthlyTarget: record.monthlyTarget,
      commissionRatePercent: record.commissionRatePercent,
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const common = {
      monthlyTarget: values.monthlyTarget != null ? String(values.monthlyTarget) : undefined,
      commissionRatePercent: values.commissionRatePercent != null ? String(values.commissionRatePercent) : undefined,
    };
    try {
      if (editing) {
        const payload = { name: values.name, ...common };
        if (values.password) payload.password = values.password;
        await updateSalesPersonApi(editing.id, payload);
        message.success("Sales account updated");
      } else {
        await createSalesPersonApi({ name: values.name, email: values.email, password: values.password, ...common });
        message.success("Sales account created");
      }
      setOpen(false);
      fetchPeople();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to save sales account");
    }
  };

  const handleToggleActive = async (record) => {
    try {
      await updateSalesPersonApi(record.id, { active: String(!record.isActive) });
      message.success(record.isActive ? "Account deactivated" : "Account activated");
      fetchPeople();
    } catch {
      message.error("Failed to update status");
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Deactivate this sales account?",
      content: "They will no longer be able to log in.",
      okText: "Deactivate",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteSalesPersonApi(id);
          message.success("Sales account deactivated");
          fetchPeople();
        } catch (err) {
          message.error(err?.response?.data?.data || "Failed to deactivate");
        }
      },
    });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 600, color: "#111827" }}>{v}</div>
          <div style={{ fontSize: 11, color: "#9CA3AF" }}>{r.email}</div>
        </div>
      ),
    },
    {
      title: "Monthly Target",
      dataIndex: "monthlyTarget",
      align: "center",
      render: (v) => v ?? "-",
    },
    {
      title: "Commission Rate",
      dataIndex: "commissionRatePercent",
      align: "center",
      render: (v) => (v != null ? `${v}%` : "-"),
    },
    {
      title: "Vendors Onboarded",
      dataIndex: "totalVendorsOnboarded",
      align: "center",
      render: (v) => v ?? 0,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      width: 100,
      align: "center",
      render: (v) => <Tag color={v ? "success" : "default"}>{v ? "Active" : "Inactive"}</Tag>,
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit / Reset Password", icon: <EditOutlined />, onClick: () => openEdit(r) },
              {
                key: "toggle",
                label: r.isActive ? "Deactivate" : "Activate",
                icon: <PoweroffOutlined />,
                onClick: () => handleToggleActive(r),
              },
              { type: "divider" },
              { key: "delete", label: "Deactivate", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r.id) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Sales Team</div>
          <div className="page-sub">{people.length} sales accounts — can onboard vendors for your approval</div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ background: ACCENT, border: "none", borderRadius: 8 }}
          onClick={openAdd}
        >
          Add Sales Account
        </Button>
      </div>

      <AppTable columns={columns} data={people} />

      <Modal
        title={editing ? `Edit — ${editing.name}` : "Add Sales Account"}
        open={open}
        okText={editing ? "Save Changes" : "Create Account"}
        okButtonProps={{ style: { background: ACCENT, border: "none" } }}
        onOk={handleSubmit}
        onCancel={() => { setOpen(false); setEditing(null); form.resetFields(); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <AppInput name="name" label="Name" rules={[{ required: true, message: "Name is required" }]} />
          {!editing && (
            <AppInput
              name="email"
              label="Login Email"
              rules={[{ required: true, message: "Login email is required" }, { type: "email", message: "Enter a valid email" }]}
            />
          )}
          <Form.Item
            name="password"
            label={editing ? "New Password (leave blank to keep unchanged)" : "Password"}
            rules={editing ? [] : [{ required: true, message: "Password is required" }]}
          >
            <Input.Password placeholder="Password for the sales account's login" />
          </Form.Item>

          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="monthlyTarget" label="Monthly Target (vendors)" style={{ flex: 1 }} rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: "100%", borderRadius: 8 }} />
            </Form.Item>
            <Form.Item name="commissionRatePercent" label="Commission Rate (%)" style={{ flex: 1 }} rules={[{ required: true }]}>
              <InputNumber min={0} max={100} step={0.5} style={{ width: "100%", borderRadius: 8 }} />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
